import { computed, Injectable, signal } from '@angular/core';
import type { User } from '@shared/modules/users/user';
import { UserPresenter } from '@shared/modules/users/user.presenter';
import { UserFetcher } from '../../modules/users/user.fetcher';
import { AppEvents } from '../event-emitter/app-events.service';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class CurrentUser {
    private readonly _currentUser = signal<User | undefined>(undefined);

    public readonly user = this._currentUser.asReadonly();
    public readonly user$ = toObservable(this._currentUser);

    public readonly currentUserId = computed(() => {
        return this._currentUser()?.id ?? null;
    });

    public readonly supportsPasswordPrompts = computed(() => {
        return this._currentUser()?.supports_password_prompts ?? true;
    })

    get id() {
        return this.currentUserId();
    }

    constructor(
        private eventEmitter: AppEvents,
        private userFetcher: UserFetcher,
    ) {
        this._currentUser.set(UserPresenter.attachItem(window.CareLineLive?.user));

        this.userFetcher.successEvents.subscribe(event => {
            if (event.data.id === this.currentUserId()) {
                this._fetchCurrentUser();
            }
        });
    }

    private _fetchCurrentUser() {
        return this.userFetcher.current().subscribe(response => {
            this._currentUser.set(UserPresenter.attachItem(response.data));
        });
    }

    get currentUser(): User {
        return this._currentUser();
    }

    get value(): User {
        return this._currentUser();
    }

    refresh(): void {
        this._fetchCurrentUser();
    }
}
